import { useCallback, useEffect, useRef, useState } from 'react'

interface SplitPaneProps {
  left: React.ReactNode
  right: React.ReactNode
  /** Initial left width as a percentage of the container. */
  initialPercent?: number
}

const MIN_PERCENT = 24
const MAX_PERCENT = 78
const STEP_PERCENT = 2

/**
 * Vertical split with a draggable + keyboard-accessible divider.
 * Alt+Left/Right nudge it for the keyboard-first workflow.
 */
export function SplitPane({ left, right, initialPercent = 46 }: SplitPaneProps) {
  const [percent, setPercent] = useState(initialPercent)
  const containerRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef<{ startX: number; startPercent: number } | null>(null)

  const clamp = (value: number) => Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, value))

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    draggingRef.current = { startX: event.clientX, startPercent: percent }
  }, [percent])

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current
    const container = containerRef.current
    if (!drag || !container) return
    const width = container.getBoundingClientRect().width
    if (width <= 0) return
    setPercent(clamp(drag.startPercent + ((event.clientX - drag.startX) / width) * 100))
  }, [])

  const endDrag = useCallback(() => {
    draggingRef.current = null
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setPercent((p) => clamp(p - STEP_PERCENT))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setPercent((p) => clamp(p + STEP_PERCENT))
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 overflow-hidden">
      <div style={{ width: `${percent}%` }} className="flex min-w-0 flex-col border-r border-edge bg-ink-950">
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panes (Alt+Left / Alt+Right)"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="group -mx-1 w-2 shrink-0 cursor-col-resize bg-transparent outline-none hover:bg-sync/20 focus-visible:bg-sync/30 active:bg-sync/40"
      />
      <div className="relative min-w-0 flex-1 bg-ink-900">{right}</div>
    </div>
  )
}
